'use client';
import { Button } from '@curology/ui-components-web/react/Button';
import { useAtom } from 'jotai';
import React, { useCallback, useEffect, useState } from 'react';
import { type PageModel, type SurveyModel } from 'survey-core';
import { type CurrentPageChangingEvent } from 'survey-core/typings/survey-events-api';

import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { getBailout } from '@/components/flow/steps/survey/surveyjsSurvey/surveyjs/utils/bailOutHandler';
import { useRouter } from '@/hooks/useRouter';

import { autoAdvancingAtom } from '../../../atoms/autoAdvancingAtom';

const ForwardButton = ({ model }: { model: SurveyModel }) => {
  const { brand, treatmentCategory } = useFlowContext();

  const router = useRouter();
  const onValueChanged = useCallback(
    (survey: SurveyModel, options: { value: any }) => {
      if (options.value) {
        setButtonText(survey.pageNextText);
      }

      setIsDisabled(!survey.isCurrentPageValid);
    },
    [],
  );

  const onCurrentPageChanging = (
    survey: SurveyModel,
    options: CurrentPageChangingEvent,
  ) => {
    if (options.newCurrentPage.validate()) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  };

  useEffect(() => {
    const onCurrentPageChanged = (
      sender: SurveyModel,
      options: { newCurrentPage: PageModel },
    ) => {
      const question = options.newCurrentPage.questions[0];

      if (question.isRequired) {
        setButtonText(model.pageNextText);
        return;
      }

      setButtonText(question.isAnswered ? model.pageNextText : 'Skip');
    };

    if (!model.onValueChanged.hasFunc(onValueChanged)) {
      model.onValueChanged.add(onValueChanged);
    }
    if (!model.onCurrentPageChanging.hasFunc(onCurrentPageChanging)) {
      model.onCurrentPageChanging.add(onCurrentPageChanging);
    }
    if (!model.onCurrentPageChanged.hasFunc(onCurrentPageChanged)) {
      model.onCurrentPageChanged.add(onCurrentPageChanged);
    }
  }, [
    model.onCurrentPageChanged,
    model.onCurrentPageChanging,
    model.onValueChanged,
    model.pageNextText,
    onValueChanged,
  ]);

  const [isDisabled, setIsDisabled] = useState(!model.isCurrentPageValid);
  const [buttonText, setButtonText] = useState(model.pageNextText ?? 'Next');
  const [isAutoAdvancing] = useAtom(autoAdvancingAtom);

  const handleOnClick = async () => {
    // @ts-expect-error Get current page question
    const currentQuestion = model.getCurrentPageQuestions()[0];
    setIsDisabled(true);

    const bailoutRoute = await getBailout(
      treatmentCategory ?? 'skin',
      currentQuestion,
      brand,
    );

    if (bailoutRoute) {
      router.push(bailoutRoute);
    } else {
      model.nextPage() || model.completeLastPage();
    }

    setIsDisabled(!model.isCurrentPageValid);
  };
  return (
    <Button
      onClick={handleOnClick}
      className="cur-w-full"
      disabled={isDisabled}
      loading={isAutoAdvancing}
    >
      {buttonText}
    </Button>
  );
};

export default ForwardButton;
